import React, { useEffect } from 'react';
import OperatorConsole from './OperatorConsole';
import CapabilityCard from './CapabilityCard';
import ScouterTerminal from './ScouterTerminal';
import { Dialer, CallWaveform } from './Softphone';
import { SCOUTER_ASCII_ART } from './ScouterAscii';
import { assetUrl } from '../utils/assets';

const capabilities = {
  leads: {
    title: 'Finds Your Leads',
    description: 'Scans local business data, qualifies prospects, and launches outreach automatically. No manual list-building.',
    stats: [
      { value: '500+', label: 'Leads Per City' },
      { value: '~$0.03', label: 'Cost Per Lead' },
      { value: '0', label: 'Manual Work' }
    ]
  },
  phones: {
    title: 'Runs The Phones',
    description: 'Your AI phone. It answers every inbound call and makes outbound calls for reminders and follow-ups. No hold music, no missed calls.',
    stats: [
      { value: '99.2%', label: 'Accuracy' },
      { value: '75%', label: 'Automation' },
      { value: '0.4s', label: 'API Speed' }
    ]
  },
  inPerson: {
    title: 'Engages In Person',
    description: 'Replaces the clipboard, the wait, and the register. Guests scan a QR code to check in, join the queue, and pay. No app download, no front desk bottleneck.',
    stats: [
      { value: '3.2×', label: 'Faster Check-In' },
      { value: '40%', label: 'Fewer Interruptions' },
      { value: '<60s', label: 'Location Setup' }
    ]
  }
};

const HowItWorks = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="hiw-page">

      {/* Intro */}
      <section className="hiw-hero">
        <div className="hiw-container">
          <div className="hiw-eyebrow">
            <span className="hiw-eyebrow-dot"></span>
            Full-Stack Business Automation
          </div>
          <h1 className="hiw-title">How It Works</h1>
          <p className="hiw-subtitle">
            Destiny finds your leads, handles every call, and welcomes your guests: three capabilities, one platform, running 24/7.
          </p>
        </div>
      </section>

      {/* ── TRACK 1: FIND LEADS ── */}
      <section className="hiw-track">
        <div className="hiw-container">
          <div className="hiw-track-header">
            <span className="hiw-track-label">01 · Find Leads</span>
            <h2 className="hiw-track-headline">Automated from target to inbox.</h2>
            <p className="hiw-track-sub">You tell Destiny who you&rsquo;re selling to. It handles everything from discovery to a reply in your inbox.</p>
          </div>
        </div>

        <div className="leads-phone-section" style={{ padding: 'var(--spacing-xl) 0' }}>
          <div className="leads-phone-ascii-bg" aria-hidden="true">
            <pre>{SCOUTER_ASCII_ART}</pre>
          </div>
          <div className="hiw-container leads-phone-content">
            <CapabilityCard {...capabilities.leads} />
            <ScouterTerminal />
          </div>
        </div>

        <div className="hiw-stats-band">
          <div className="hiw-container">
            <div className="hiw-stats-grid">
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">500+</div>
                <div className="hiw-stat-label">Leads per City</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">$0.03</div>
                <div className="hiw-stat-label">Cost per Lead</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">30s</div>
                <div className="hiw-stat-label">Site Deploy Time</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">0</div>
                <div className="hiw-stat-label">Manual Work</div>
              </div>
            </div>
          </div>
        </div>

        <section className="hiw-features">
          <div className="hiw-container">
            <h2 className="hiw-section-title">Capabilities</h2>
            <div className="hiw-feature-grid">

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </div>
                <h3>Maps Coverage</h3>
                <p>Breaks past Google&rsquo;s 60-result cap by tiling city bounding boxes into overlapping grids. Every business, in any industry, in any city.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <h3>AI-Written Outreach</h3>
                <p>AI reads each lead&rsquo;s real reviews, rating, and data, then writes an email that sounds like you researched them personally. Not a template. A real message.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                </div>
                <h3>Lead Intelligence Reports</h3>
                <p>Export a premium intelligence report of every scraped lead (contacts, ratings, reviews, and insights) as a polished PDF.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.77-.77a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/>
                  </svg>
                </div>
                <h3>Voice &amp; SMS Outreach</h3>
                <p>AI voice calls and SMS fallback. If no email is found, Destiny steps in automatically. Outreach runs while you sleep.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M3 9h18"/>
                    <path d="M9 21V9"/>
                  </svg>
                </div>
                <h3>Website Pipeline</h3>
                <p>Generates a fully custom site for every lead using their real reviews, photos, and info. Deployed instantly. Not a template, a real site.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                    <polyline points="17 6 23 6 23 12"/>
                  </svg>
                </div>
                <h3>Auto-Expiring Previews</h3>
                <p>Preview sites auto-delete after 14 days. Converts keep their site. Your server stays clean.</p>
              </div>

            </div>
          </div>
        </section>

        <section className="hiw-steps-section">
          <div className="hiw-container">
            <h2 className="hiw-section-title">How It Works</h2>
            <div className="hiw-steps-wrap">

              <div className="hiw-step-item">
                <div className="hiw-step-num">1</div>
                <div className="hiw-step-content">
                  <h3>Target</h3>
                  <p>Tell Destiny your industry and city. It grid-tiles Google Maps to surface every matching business. No cap, no gaps.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">2</div>
                <div className="hiw-step-content">
                  <h3>Research</h3>
                  <p>Every lead gets profiled using their real Google data: reviews, star rating, location, and hours. Destiny builds a full picture before a single word is written.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">3</div>
                <div className="hiw-step-content">
                  <h3>Write</h3>
                  <p>A unique message gets written for every single business, personalized enough to feel like you spent 20 minutes on each one.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">4</div>
                <div className="hiw-step-content">
                  <h3>Send</h3>
                  <p>Destiny tracks down the owner&rsquo;s email. Every message goes out automatically. You wake up to replies.</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </section>

      {/* ── TRACK 2: CLOSE THE CALL ── */}
      <section className="hiw-track">
        <div className="hiw-container">
          <div className="hiw-track-header">
            <span className="hiw-track-label">02 · Close The Call</span>
            <h2 className="hiw-track-headline">Your front desk, available 24/7.</h2>
            <p className="hiw-track-sub">Destiny handles inbound calls, books appointments, runs outbound campaigns, and follows up, automatically, around the clock.</p>
          </div>
        </div>

        <div className="hiw-container">
          <OperatorConsole />
        </div>

        <div className="hiw-container">
          <CapabilityCard {...capabilities.phones} />
          <div className="sp-pairing">
            <Dialer />
            <CallWaveform />
          </div>
        </div>

        <div className="hiw-stats-band">
          <div className="hiw-container">
            <div className="hiw-stats-grid">
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">24/7</div>
                <div className="hiw-stat-label">Always Answering</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">&lt;2s</div>
                <div className="hiw-stat-label">IVR Response Time</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">HD</div>
                <div className="hiw-stat-label">Neural Voice Quality</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">100%</div>
                <div className="hiw-stat-label">Calls Handled</div>
              </div>
            </div>
          </div>
        </div>

        <section className="hiw-features">
          <div className="hiw-container">
            <h2 className="hiw-section-title">Core Capabilities</h2>
            <div className="hiw-feature-grid">

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.77-.77a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/>
                  </svg>
                </div>
                <h3>Intelligent Inbound Handling</h3>
                <p>Every inbound call is answered instantly. Destiny greets callers, collects their intent via voice or keypad, routes them to the right outcome, and transfers to a live agent when needed. No hold music, no missed calls.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </div>
                <h3>Automated Outbound Campaigns</h3>
                <p>Reach your entire contact list automatically. Appointment reminders, follow-ups, lead outreach: Destiny places the calls with a natural HD voice, detects voicemails, and sends SMS fallbacks on no-answers.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M18 20V10"/>
                    <path d="M12 20V4"/>
                    <path d="M6 20v-6"/>
                    <path d="M2 20h20"/>
                    <circle cx="18" cy="7" r="3"/>
                    <polyline points="16.5 7 18 5.5 19.5 7"/>
                  </svg>
                </div>
                <h3>Smart Webhook Automation</h3>
                <p>When a call reaches a key moment, Destiny fires a webhook to any external system. Book appointments, create support tickets, place orders, or look up a status, and the response gets read back to the caller instantly.</p>
              </div>

            </div>
          </div>
        </section>

        <section className="hiw-use-cases-section">
          <div className="hiw-container">
            <h2 className="hiw-section-title">Built for Real Teams</h2>
            <div className="hiw-use-case-grid">

              <div className="hiw-use-case-card">
                <div className="hiw-use-case-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8"  y1="2" x2="8"  y2="6"/>
                    <line x1="3"  y1="10" x2="21" y2="10"/>
                    <polyline points="9 16 11 18 15 14"/>
                  </svg>
                </div>
                <div className="hiw-use-case-body">
                  <span className="hiw-use-case-tag">Healthcare</span>
                  <h3 className="hiw-use-case-title">Appointment Booking</h3>
                  <p className="hiw-use-case-desc">Callers say their preferred date and time. Destiny books it directly into your scheduling system and reads back a confirmation number. No hold music, no back and forth.</p>
                </div>
              </div>

              <div className="hiw-use-case-card">
                <div className="hiw-use-case-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.77-.77a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 17z"/>
                  </svg>
                </div>
                <div className="hiw-use-case-body">
                  <span className="hiw-use-case-tag">Any Business</span>
                  <h3 className="hiw-use-case-title">Inbound Call Routing</h3>
                  <p className="hiw-use-case-desc">Handle every inbound call intelligently. Greet callers by name, collect their intent, and route them to the right outcome or live agent in seconds.</p>
                </div>
              </div>

              <div className="hiw-use-case-card">
                <div className="hiw-use-case-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    <line x1="9"  y1="10" x2="15" y2="10"/>
                    <line x1="12" y1="7"  x2="12" y2="13"/>
                  </svg>
                </div>
                <div className="hiw-use-case-body">
                  <span className="hiw-use-case-tag">Support</span>
                  <h3 className="hiw-use-case-title">Ticket Creation</h3>
                  <p className="hiw-use-case-desc">Callers describe their issue in plain speech. Destiny creates a support ticket, assigns priority based on keywords, and reads back a ticket number with an estimated response time.</p>
                </div>
              </div>

              <div className="hiw-use-case-card">
                <div className="hiw-use-case-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <rect x="1" y="4" width="22" height="16" rx="2"/>
                    <line x1="1"  y1="10" x2="23" y2="10"/>
                    <line x1="7"  y1="15" x2="9"  y2="15"/>
                    <line x1="11" y1="15" x2="13" y2="15"/>
                  </svg>
                </div>
                <div className="hiw-use-case-body">
                  <span className="hiw-use-case-tag">E-Commerce &amp; Services</span>
                  <h3 className="hiw-use-case-title">Order Placement &amp; Status</h3>
                  <p className="hiw-use-case-desc">Callers place orders or check order status entirely through the IVR. Destiny sends a payment link via SMS or creates an invoice for follow-up, configured per tenant.</p>
                </div>
              </div>

              <div className="hiw-use-case-card">
                <div className="hiw-use-case-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="11" y1="8"  x2="11" y2="14"/>
                    <line x1="8"  y1="11" x2="14" y2="11"/>
                  </svg>
                </div>
                <div className="hiw-use-case-body">
                  <span className="hiw-use-case-tag">Sales</span>
                  <h3 className="hiw-use-case-title">Outbound Lead Campaigns</h3>
                  <p className="hiw-use-case-desc">Upload a contact list, build a script, and launch. Destiny dials in parallel, handles responses via DTMF routing, and escalates warm leads to your team in real time.</p>
                </div>
              </div>

              <div className="hiw-use-case-card">
                <div className="hiw-use-case-icon-wrap">
                  <svg viewBox="0 0 24 24">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="hiw-use-case-body">
                  <span className="hiw-use-case-tag">Operations</span>
                  <h3 className="hiw-use-case-title">Live Agent Transfer</h3>
                  <p className="hiw-use-case-desc">When a personal touch is needed, Destiny transfers the caller to a live agent with full context, warm, blind, or conference transfer, all from the same platform.</p>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section className="hiw-steps-section">
          <div className="hiw-container">
            <h2 className="hiw-section-title">How It Works</h2>
            <div className="hiw-steps-wrap">

              <div className="hiw-step-item">
                <div className="hiw-step-num">1</div>
                <div className="hiw-step-content">
                  <h3>Build Your Script</h3>
                  <p>Use the Script Builder to write your conversation flow in plain text. Add collect nodes to capture caller input, branch on keypad or speech, and attach webhook actions to any node. Variables like <code>{'{{firstName}}'}</code> are auto-filled from your contacts at call time.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">2</div>
                <div className="hiw-step-content">
                  <h3>Configure Your Automations</h3>
                  <p>In the Node Configuration panel, attach collect nodes to capture dates, times, issue descriptions, or order details. Wire up webhook actions that fire to your booking system, ticketing tool, or order API when the call reaches a key node.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">3</div>
                <div className="hiw-step-content">
                  <h3>Launch Inbound or Outbound</h3>
                  <p>For outbound: upload contacts and start a campaign. For inbound: point your number at Destiny and every call is handled automatically. Both modes run simultaneously on the Full Suite plan.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">4</div>
                <div className="hiw-step-content">
                  <h3>Monitor Everything Live</h3>
                  <p>Watch calls process in real time on your dashboard. Replay recordings, inspect IVR path summaries, review webhook payloads, and track usage across minutes, SMS, and automated actions.</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </section>

      {/* ── TRACK 3: ENGAGE IN PERSON ── */}
      <section className="hiw-track">
        <div className="hiw-container">
          <div className="hiw-track-header">
            <span className="hiw-track-label">03 · Engage In Person</span>
            <h2 className="hiw-track-headline">Every guest, perfectly welcomed.</h2>
            <p className="hiw-track-sub">Replaces the clipboard, the shout, and the awkward wait. Give your guests a frictionless arrival and your team a queue they can actually manage.</p>
          </div>
        </div>

        <div className="hiw-container">
          <CapabilityCard {...capabilities.inPerson} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="image-wrapper product-frame" style={{
              maxWidth: '420px',
              width: '100%',
              borderRadius: '24px',
              overflow: 'hidden',
              border: '1px solid var(--glass-border)',
              boxShadow: '0 24px 56px rgba(0,0,0,0.35)',
              background: 'rgba(255, 255, 255, 0.02)'
            }}>
              <img
                src={assetUrl('/images/bridget-kiosk.jpg')}
                alt="Bridget check-in kiosk on a marble counter"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          </div>
        </div>

        <div className="hiw-stats-band">
          <div className="hiw-container">
            <div className="hiw-stats-grid">
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">3.2×</div>
                <div className="hiw-stat-label">Faster Check-In</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">40%</div>
                <div className="hiw-stat-label">Fewer Interruptions</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">&lt;60s</div>
                <div className="hiw-stat-label">Location Setup</div>
              </div>
              <div className="hiw-stat-item">
                <div className="hiw-stat-number">99.9%</div>
                <div className="hiw-stat-label">Uptime</div>
              </div>
            </div>
          </div>
        </div>

        <section className="hiw-features">
          <div className="hiw-container">
            <h2 className="hiw-section-title">Capabilities</h2>
            <div className="hiw-feature-grid">

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M3 9h18"/>
                    <path d="M9 21V9"/>
                  </svg>
                </div>
                <h3>Instant QR Check-In</h3>
                <p>Guests scan a code at the door and join your queue in seconds. No app download, no friction, no front desk bottleneck.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M18 20V10"/>
                    <path d="M12 20V4"/>
                    <path d="M6 20v-6"/>
                    <path d="M2 20h20"/>
                  </svg>
                </div>
                <h3>Live Analytics</h3>
                <p>Track wait times, peak hours, and service velocity across every location. Surface insights that actually change how you operate.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M7 7h.01M12 7h.01M17 7h.01M7 12h.01M12 12h.01M17 12h.01M7 17h.01M12 17h.01M17 17h.01"/>
                  </svg>
                </div>
                <h3>Custom Flow Builder</h3>
                <p>Design your own check-in journey. Collect the fields you need, ask the right questions, and route guests to the right queue automatically.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <rect x="2" y="3" width="20" height="14" rx="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </div>
                <h3>Display Screens</h3>
                <p>Cast your live queue to any TV or monitor. Guests see their position in real time, reducing perceived wait and front desk anxiety.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <h3>Multi-Location</h3>
                <p>Manage every venue from one dashboard. Each location gets its own flow, branding, and queue, unified under your business account.</p>
              </div>

              <div className="hiw-feature-card">
                <div className="hiw-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'20px',height:'20px',color:'var(--hiw-green)'}}>
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </div>
                <h3>Seamless Integrations</h3>
                <p>Connect to the tools you already use. Destiny plugs into your existing stack without forcing a workflow overhaul.</p>
              </div>

            </div>
          </div>
        </section>

        <section className="hiw-steps-section">
          <div className="hiw-container">
            <h2 className="hiw-section-title">How It Works</h2>
            <div className="hiw-steps-wrap">

              <div className="hiw-step-item">
                <div className="hiw-step-num">1</div>
                <div className="hiw-step-content">
                  <h3>Set Up Your Location</h3>
                  <p>Create your venue in minutes. Customize your check-in flow, branding, and queue logic with zero code.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">2</div>
                <div className="hiw-step-content">
                  <h3>Guests Check In</h3>
                  <p>A QR code at your door is all it takes. Guests join the queue from their phone, instantly.</p>
                </div>
              </div>

              <div className="hiw-step-item">
                <div className="hiw-step-num">3</div>
                <div className="hiw-step-content">
                  <h3>Your Team Flows</h3>
                  <p>Call the next guest, see live wait data, and keep service moving, all from one clean interface.</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </section>

      <footer>
        <p>&copy; 2026 TAVERNIX SYSTEMS // EST_2024</p>
        <p>Powered by Destiny</p>
      </footer>

    </div>
  );
};

export default HowItWorks;
